/**
 * Question Result Screen Component
 * Shows the results after a question:
 * - The correct answer revealed
 * - Who answered correctly/incorrectly
 * - Who was the fastest
 * - Fun animations for correct/wrong answers
 */

import React, { useEffect, useRef } from "react";
import {
  View,
  Text,
  ScrollView,
  Animated,
  Easing,
  Dimensions,
  StyleSheet,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import LottieView from "lottie-react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { getLottieSource, getLottieByKey } from "@/lib/lottie-registry";
import { QuestionResult, ParticipantAnswerResult, QuizQuestion, LiveQuizParticipant } from "../types";

const { width: SCREEN_WIDTH } = Dimensions.get("window");

interface QuestionResultScreenProps {
  /** The question that was answered */
  question: QuizQuestion;
  /** Question number (1-indexed) */
  questionNumber: number;
  /** Total questions */
  totalQuestions: number;
  /** Result data with correct answer info */
  result: QuestionResult;
  /** Individual participant results */
  participantResults: ParticipantAnswerResult[];
  /** All participants for mascot/team info */
  participants: LiveQuizParticipant[];
  /** Quiz theme color */
  quizColor?: string;
  /** Whether the current user's answer was correct */
  myAnswerCorrect?: boolean;
}

/**
 * Question Result Screen - Shows who answered correctly and who was fastest
 */
export const QuestionResultScreen: React.FC<QuestionResultScreenProps> = ({
  question,
  questionNumber,
  totalQuestions,
  result,
  participantResults,
  participants,
  quizColor = "#8B5CF6",
  myAnswerCorrect,
}) => {
  const colors = useThemeColors();
  
  // Animations
  const correctAnswerAnim = useRef(new Animated.Value(0)).current;
  const fastestAnim = useRef(new Animated.Value(0)).current;
  const resultsAnim = useRef(new Animated.Value(0)).current;
  
  // Run entrance animations
  useEffect(() => {
    // Stagger entrance animations
    Animated.stagger(300, [
      Animated.spring(correctAnswerAnim, {
        toValue: 1,
        tension: 50,
        friction: 8,
        useNativeDriver: true,
      }),
      Animated.spring(fastestAnim, {
        toValue: 1,
        tension: 50,
        friction: 8,
        useNativeDriver: true,
      }),
      Animated.spring(resultsAnim, {
        toValue: 1,
        tension: 50,
        friction: 8,
        useNativeDriver: true,
      }),
    ]).start();
  }, [correctAnswerAnim, fastestAnim, resultsAnim]);
  
  // Get correct answer text
  const correctAnswer = question.options[result.correct_option_index];
  
  // Find fastest correct participant
  const fastestParticipant = result.fastest_correct
    ? participants.find(p => p.id === result.fastest_correct?.participant_id)
    : null;
  
  // Get Lottie sources
  const checkmarkLottie = getLottieSource("checkmark");
  const trophyLottie = getLottieSource("head_with_crown_emoji");
  const confettiLottie = getLottieSource("confetti");
  const sadLottie = getLottieSource("crying_emoji");
  
  // Sort participants by correct first, then by time
  const sortedResults = [...participantResults].sort((a, b) => {
    // Correct answers first
    if (a.isCorrect && !b.isCorrect) return -1;
    if (!a.isCorrect && b.isCorrect) return 1;
    // Then by time
    return a.timeTakenMs - b.timeTakenMs;
  });
  
  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Show confetti if user got it right */}
      {myAnswerCorrect && confettiLottie && (
        <View style={styles.confettiContainer}>
          <LottieView
            source={confettiLottie}
            autoPlay
            loop={false}
            style={styles.confettiLottie}
          />
        </View>
      )}
      
      <ScrollView 
        style={styles.scrollView} 
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Question indicator */}
        <View style={styles.questionIndicator}>
          <Text style={[styles.questionIndicatorText, { color: colors.textSecondary }]}>
            Vraag {questionNumber} van {totalQuestions}
          </Text>
        </View>
        
        {/* The Question */}
        <View style={[styles.questionCard, { backgroundColor: colors.surface }]}>
          <Text style={[styles.questionText, { color: colors.text }]}>
            {question.question}
          </Text>
        </View>
        
        {/* Correct Answer Reveal */}
        <Animated.View
          style={[
            styles.correctAnswerCard,
            {
              backgroundColor: "#10B981",
              transform: [
                { scale: correctAnswerAnim },
                {
                  translateY: correctAnswerAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [-20, 0],
                  }),
                },
              ],
              opacity: correctAnswerAnim,
            },
          ]}
        >
          <View style={styles.correctAnswerHeader}>
            {checkmarkLottie && (
              <LottieView
                source={checkmarkLottie}
                autoPlay
                loop={false}
                style={styles.checkmarkLottie}
              />
            )}
            <Text style={styles.correctAnswerLabel}>Het juiste antwoord is:</Text>
          </View>
          <Text style={styles.correctAnswerText}>
            {correctAnswer?.text || "Geen antwoord"}
          </Text>
        </Animated.View>
        
        {/* Fastest Correct Answer */}
        {result.fastest_correct && fastestParticipant && (
          <Animated.View
            style={[
              styles.fastestCard,
              {
                backgroundColor: "#F59E0B20",
                borderColor: "#F59E0B",
                transform: [{ scale: fastestAnim }],
                opacity: fastestAnim,
              },
            ]}
          >
            <View style={styles.fastestHeader}>
              {trophyLottie && (
                <LottieView
                  source={trophyLottie}
                  autoPlay
                  loop
                  style={styles.trophyLottie}
                />
              )}
              <Text style={styles.fastestLabel}>⚡ Snelste juiste antwoord!</Text>
            </View>
            
            <View style={styles.fastestInfo}>
              {/* Mascot */}
              {fastestParticipant.mascot_key && (
                <View style={styles.fastestMascot}>
                  {(() => {
                    const lottie = getLottieByKey(fastestParticipant.mascot_key);
                    return lottie?.source ? (
                      <LottieView
                        source={lottie.source}
                        autoPlay
                        loop
                        style={{ width: 60, height: 60 }}
                      />
                    ) : null;
                  })()}
                </View>
              )}
              
              <View style={styles.fastestDetails}>
                <Text style={[styles.fastestTeamName, { color: colors.text }]}>
                  {fastestParticipant.team?.name || "Team"}
                </Text>
                <Text style={[styles.fastestTime, { color: colors.textSecondary }]}>
                  {(result.fastest_correct.time_ms / 1000).toFixed(2)}s
                </Text>
              </View>
              
              <View style={styles.fastestPoints}>
                <Text style={styles.fastestPointsText}>
                  +{result.points_awarded.find(p => p.participant_id === result.fastest_correct?.participant_id)?.points || 0}
                </Text>
                <Text style={styles.fastestBonusText}>
                  +{result.points_awarded.find(p => p.participant_id === result.fastest_correct?.participant_id)?.bonus || 0} bonus
                </Text>
              </View>
            </View>
          </Animated.View>
        )}
        
        {/* All Participants Results */}
        <Animated.View
          style={[
            styles.allResultsContainer,
            {
              transform: [{ scale: resultsAnim }],
              opacity: resultsAnim,
            },
          ]}
        >
          <Text style={[styles.allResultsTitle, { color: colors.text }]}>
            Alle antwoorden
          </Text>
          
          {sortedResults.map((pResult, index) => {
            const participant = participants.find(p => p.id === pResult.participantId);
            const mascotLottie = participant?.mascot_key 
              ? getLottieByKey(participant.mascot_key) 
              : null;
            
            return (
              <View
                key={pResult.participantId}
                style={[
                  styles.resultRow,
                  {
                    backgroundColor: pResult.isCorrect 
                      ? "#10B98115" 
                      : "#EF444415",
                    borderColor: pResult.isCorrect 
                      ? "#10B981" 
                      : "#EF4444",
                  },
                ]}
              >
                {/* Correct/Wrong indicator */}
                <View style={[
                  styles.resultIndicator,
                  { backgroundColor: pResult.isCorrect ? "#10B981" : "#EF4444" },
                ]}>
                  <Ionicons 
                    name={pResult.isCorrect ? "checkmark" : "close"} 
                    size={20} 
                    color="#FFFFFF" 
                  />
                </View>
                
                {/* Mascot */}
                <View style={styles.resultMascot}>
                  {mascotLottie?.source ? (
                    <LottieView
                      source={mascotLottie.source}
                      autoPlay
                      loop
                      style={{ width: 40, height: 40 }}
                    />
                  ) : (
                    <Text style={{ fontSize: 24 }}>🐾</Text>
                  )}
                </View>
                
                {/* Team name */}
                <View style={styles.resultInfo}>
                  <Text style={[styles.resultTeamName, { color: colors.text }]}>
                    {pResult.teamName}
                  </Text>
                  <Text style={[styles.resultTime, { color: colors.textSecondary }]}>
                    {pResult.selectedOption !== null 
                      ? `${(pResult.timeTakenMs / 1000).toFixed(2)}s`
                      : "Geen antwoord"
                    }
                  </Text>
                </View>
                
                {/* Points */}
                <View style={styles.resultPoints}>
                  <Text style={[
                    styles.resultPointsText,
                    { color: pResult.isCorrect ? "#10B981" : "#EF4444" },
                  ]}>
                    {pResult.isCorrect 
                      ? `+${pResult.pointsEarned + pResult.bonusEarned}` 
                      : "0"
                    }
                  </Text>
                </View>
              </View>
            );
          })}
        </Animated.View>
        
        {/* My Result feedback */}
        {myAnswerCorrect !== undefined && (
          <View style={[
            styles.myResultBanner,
            { backgroundColor: myAnswerCorrect ? "#10B98120" : "#EF444420" },
          ]}>
            {myAnswerCorrect ? (
              <>
                <Text style={styles.myResultEmoji}>🎉</Text>
                <Text style={[styles.myResultText, { color: "#10B981" }]}>
                  Goed gedaan!
                </Text>
              </>
            ) : (
              <>
                <Text style={styles.myResultEmoji}>😅</Text>
                <Text style={[styles.myResultText, { color: "#EF4444" }]}>
                  Helaas, volgende keer beter!
                </Text>
              </>
            )}
          </View>
        )}
        
        {/* Waiting message */}
        <View style={styles.waitingMessage}>
          <Text style={[styles.waitingText, { color: colors.textSecondary }]}>
            Wacht op de Quiz Master...
          </Text>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  confettiContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
    pointerEvents: "none",
  },
  confettiLottie: {
    width: "100%",
    height: "100%",
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 40,
  },
  questionIndicator: {
    alignItems: "center",
    marginBottom: 16,
  },
  questionIndicatorText: {
    fontSize: 16,
    fontWeight: "600",
  },
  questionCard: {
    padding: 20,
    borderRadius: 16,
    marginBottom: 20,
  },
  questionText: {
    fontSize: 18,
    fontWeight: "600",
    textAlign: "center",
  },
  correctAnswerCard: {
    padding: 24,
    borderRadius: 20,
    marginBottom: 20,
    alignItems: "center",
  },
  correctAnswerHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  checkmarkLottie: {
    width: 40,
    height: 40,
    marginRight: 8,
  },
  correctAnswerLabel: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "600",
  },
  correctAnswerText: {
    color: "#FFFFFF",
    fontSize: 28,
    fontWeight: "bold",
    textAlign: "center",
  },
  fastestCard: {
    padding: 20,
    borderRadius: 16,
    borderWidth: 2,
    marginBottom: 20,
  },
  fastestHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  trophyLottie: {
    width: 40,
    height: 40,
    marginRight: 8,
  },
  fastestLabel: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#F59E0B",
  },
  fastestInfo: {
    flexDirection: "row",
    alignItems: "center",
  },
  fastestMascot: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: "rgba(255,255,255,0.5)",
    overflow: "hidden",
    marginRight: 16,
  },
  fastestDetails: {
    flex: 1,
  },
  fastestTeamName: {
    fontSize: 20,
    fontWeight: "bold",
  },
  fastestTime: {
    fontSize: 14,
    marginTop: 2,
  },
  fastestPoints: {
    alignItems: "flex-end",
  },
  fastestPointsText: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#F59E0B",
  },
  fastestBonusText: {
    fontSize: 12,
    color: "#F59E0B",
  },
  allResultsContainer: {
    marginBottom: 20,
  },
  allResultsTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 12,
  },
  resultRow: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 8,
  },
  resultIndicator: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  resultMascot: {
    width: 40,
    height: 40,
    borderRadius: 20,
    overflow: "hidden",
    marginRight: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  resultInfo: {
    flex: 1,
  },
  resultTeamName: {
    fontSize: 16,
    fontWeight: "600",
  },
  resultTime: {
    fontSize: 12,
    marginTop: 2,
  },
  resultPoints: {
    alignItems: "flex-end",
  },
  resultPointsText: {
    fontSize: 18,
    fontWeight: "bold",
  },
  myResultBanner: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    borderRadius: 16,
    marginBottom: 20,
  },
  myResultEmoji: {
    fontSize: 32,
    marginRight: 12,
  },
  myResultText: {
    fontSize: 20,
    fontWeight: "bold",
  },
  waitingMessage: {
    alignItems: "center",
    paddingVertical: 20,
  },
  waitingText: {
    fontSize: 16,
    fontStyle: "italic",
  },
});

export default QuestionResultScreen;

